import { useCallback, useMemo, type FC, type VFC, type ReactNode, type ReactElement } from 'react'
import { useEffect } from 'react'
import { clearLoadingMask } from '../../framework/loadingMask'
import { Button, Title, Subtitle, InvalidPermissionsSVG, Card, CardBody } from '@panwds/react-ui'
import { PANWDS_TW3_CLS } from '../../patches/panwds-constants'
import { logout } from '../../framework/actions'
import { clearStoredExpiredTsgTokens, getCurrentTsgId, getUserFromManager, tsgUserManagers } from '../../framework/tsg'
import { getFrameworkVar } from '../../framework/states'
import { classNames } from '../../framework/utils'
import { __error } from '../../init/debug'
import { _T } from '../../i18n'
import { clearOidcSessionStorage } from './utils'
import strataLogoSvg from '../../images/strata-logo-only.svg'
import './FullscreenError.scss'

const getMessageParam = (): string => {
  const param = new URLSearchParams(window.location.search)
  return param.get('message') || ''
}

const clearSessionAfterRetry = () => {
  if (sessionStorage.reloadRetry) {
    clearOidcSessionStorage()
  }
  else {
    sessionStorage.reloadRetry = '1'
    clearStoredExpiredTsgTokens()
  }
}

const retryReload = () => {
  clearSessionAfterRetry()
  window.location.reload()
}

const LogoutButton: VFC<{}> = (): ReactElement => {
  const logoutHandler = useCallback(() => {
    logout()
  }, [])
  return <Button
    appearance='clear'
    addClassName='sparky-fullscreen-error-close'
    onClick={logoutHandler}
  >Sign Out</Button>
}

interface FullscreenErrorProps {
  message: ReactNode
  className?: string
  button?: ReactNode
  isAccessRestricted?: boolean
}

export const FullscreenError: FC<FullscreenErrorProps> = ({ children, message, button, className = 'sparky-fullscreen-error', isAccessRestricted = false }): ReactElement => {
  useEffect(clearLoadingMask, [])
  return <div className={classNames(className, PANWDS_TW3_CLS)}>
    {isAccessRestricted ? <div className='twsf-flex twsf-items-end twsf-justify-center twsf-h-2/4'><InvalidPermissionsSVG width={90} height={90} /></div> : <div className='sparky-logo'></div>}
    <Title size='xxs' level={2}>
      {children}
    </Title>
    <Subtitle>{message}</Subtitle>
    {button || <LogoutButton />}
  </div>
}

export const NoAccessFullscreenError: FC<any> = ({ className = '' }): ReactElement => {

  const url = getFrameworkVar('hub') ? 'https://assets.cdn.apps.paloaltonetworks.com/email/panw-logo.png' : strataLogoSvg

  useEffect(clearLoadingMask, [])

  const logoutHandler = useCallback(() => {
    logout()
  }, [])

  return (
    <div
      className={classNames(className, 'twsf-flex', 'twsf-flex-col', 'twsf-justify-center', PANWDS_TW3_CLS)}
      style={{
        backgroundImage: 'url(\'https://assets.cdn.apps.paloaltonetworks.com/email/email-bkg.png\')',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '0 0',
        backgroundSize: '100%',
        position: 'fixed',
        inset: '0 0 0 0',
        zIndex: 9999
      }}
    >
      <Card addClassName='twsf-m-auto twsf-overflow-hidden twsf-shadow-card' style={{ width: '400px' }}>
        <CardBody addClassName='twsf-flex twsf-flex-col twsf-gap-2'>
          <div className='twsf-p-3'>
            <img className="panw-logo" src={url} alt="Palo Alto Networks" style={{ maxWidth: '90px', maxHeight: '90px' }} />
          </div>
          <Title level={2} size='xxs'>{_T('Insufficient Privileges')}</Title>
          <Subtitle>{_T('You do not have the privileges required to access this app. If you think this is an error, please contact your organization’s administrator to obtain the required privileges.')}</Subtitle>
          <div className='twsf-flex twsf-justify-center twsf-pt-3'>
            <Button
              appearance='secondary'
              onClick={logoutHandler}
            >Close</Button>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

export const FullscreenErrorWithMessageInQuery: FC<{}> = ({ children }): ReactElement => {
  const message = useMemo(getMessageParam, [])
  return <FullscreenError message={message}>{children}</FullscreenError>
}

export const NotAuthenticated: VFC<{}> = (): ReactElement => (
  <FullscreenErrorWithMessageInQuery>{_T('Not Authenticated')}</FullscreenErrorWithMessageInQuery>
)

export const NotAuthorized: VFC<{}> = (): ReactElement => (
  <FullscreenErrorWithMessageInQuery>{_T('Not Authorized')}</FullscreenErrorWithMessageInQuery>
)

export const AuthenticatingTimeout: FC<{}> = ({ children = null }): ReactElement => (
  <FullscreenError
    message={_T('Authentication timeout occurred. Please try again. If this issue persists, please contact support.')}
    button={<Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={retryReload}
    >{_T('Try Again')}</Button>}
  >{children}</FullscreenError>
)

export const LoginFailed: VFC<{ message: ReactNode }> = ({ message }): ReactElement => (
  <FullscreenError
    message={message}
    button={<Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={retryReload}
    >{_T('Try Again')}</Button>}
  />
)

export const FailedToRenew: VFC<{}> = (): ReactElement => (
  <FullscreenError
    message={_T('Failed to keep user session, please try reload.')}
    button={<Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={retryReload}
    >{_T('Try Again')}</Button>}
  />
)

export const InactivityLogout: VFC<{ message?: string }> = ({ message }): ReactElement => (
  <FullscreenError
    message={<>
      {message || _T('You were idle for too long so we logged you out for your safety.')}
      <br />
      {_T('Please sign in again.')}
    </>}
    button={<Button
      appearance='clear'
      addClassName='sparky-fullscreen-error-close'
      onClick={retryReload}
    >{_T('Sign In')}</Button>}
  >{_T('You were logged out due to inactivity.')}</FullscreenError>
)

const retryHandler = () => {
  const param = new URLSearchParams(window.location.search)
  let path = param.get('path') || '/'
  if (!path.startsWith('/')) {
    path = '/'
  }
  if (path.startsWith('//')) {
    path = path.slice(1)
  }
  if (tsgUserManagers.none) {
    tsgUserManagers.none.signinSilent().then(async (user) => {
      if (!user?.access_token) {
        throw new Error('No access token returned')
      }
      const curTsgId = getCurrentTsgId()
      const currentUserMgr = curTsgId ? tsgUserManagers[curTsgId] : undefined
      if (currentUserMgr) {
        await getUserFromManager(currentUserMgr)
        // if (tsgUser?.access_token) {
        //   forceRerender({ immediate: true })
        // }
      }
    }).catch((err) => {
      __error('force reload due to failed to silent sign-in', err)
      clearSessionAfterRetry()
      window.location.replace(path)
    })
  }
  else {
    clearSessionAfterRetry()
    window.location.replace(path)
  }
}
const RetrySessionButton: VFC<{}> = (): ReactElement => {
  return <Button
    appearance='clear'
    addClassName='sparky-fullscreen-error-close'
    onClick={retryHandler}
  >Try Again</Button>
}

export const SessionLost: VFC<{}> = (): ReactElement => {
  return <FullscreenError
    message={_T('Please sign in again.')}
    button={<RetrySessionButton />}
  >{_T('Session Lost')}</FullscreenError>
}

export default FullscreenError
